/**
 * Channel Editor Component
 * Comprehensive editor for Channel entities with tabs for organized fields
 * Tabs: Basic | Webhook | WebAPI | Content
 */

import { LockIcon, BugIcon, LightbulbIcon, ClipboardIcon, ZapIcon } from '@bizfirst/common-react';
import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import LookupSelect from '../../Modals/LookupSelect';
import { channelApiClient } from '@flow-studio/api';
import { lookupConfigs } from '../../../lookupConfigs';
import { lookupEventBus } from 'flow-studio-core';
import { useUIStore } from '@flow-studio/store';
import { AccordionSection } from '../../Shared/AccordionSection';
import { Loader } from '../../Shared/Loader';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles
import './ChannelEditor.css';

export interface ChannelFormData {
  ChannelID?: number;
  ResID?: string;
  ChannelName: string;
  Description: string;
  ShortDescription: string;
  LongDescription: string;
  IconUrl: string;
  ImageUrl: string;
  HelpDocumentation: string;
  WebHookUrl: string;
  WebHookCode: string;
  WebHookCode2: string;
  WebHookCode3: string;
  WebHookCode4: string;
  WebApiUrl: string;
  WebApiCode: string;
  WebApiCode2: string;
  WebApiCode3: string;
  WebApiCode4: string;
  ConnectorID: number | null;
  IsActive?: boolean;
  CreatedOn?: string;
  CreatedBy?: string;
  LastModifiedOn?: string;
  LastModifiedBy?: string;
}

interface ChannelEditorProps {
  channelId?: number;
  isOpen: boolean;
  onClose: () => void;
  onChannelSaved?: (channel: ChannelFormData) => void;
}

type ChannelTab = 'basic' | 'webhook' | 'webapi' | 'content' | 'security' | 'debugger';

const defaultFormData: ChannelFormData = {
  ChannelName: '',
  Description: '',
  ShortDescription: '',
  LongDescription: '',
  IconUrl: '',
  ImageUrl: '',
  HelpDocumentation: '',
  WebHookUrl: '',
  WebHookCode: '',
  WebHookCode2: '',
  WebHookCode3: '',
  WebHookCode4: '',
  WebApiUrl: '',
  WebApiCode: '',
  WebApiCode2: '',
  WebApiCode3: '',
  WebApiCode4: '',
  ConnectorID: null,
  IsActive: true,
};

export const ChannelEditor: React.FC<ChannelEditorProps> = ({
  channelId,
  isOpen,
  onClose,
  onChannelSaved,
}) => {
  const { showToast } = useUIStore();
  const [activeTab, setActiveTab] = useState<ChannelTab>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState<ChannelFormData>(defaultFormData);

  // Load Channel on open
  useEffect(() => {
    if (isOpen) {
      if (channelId) {
        loadChannelData();
        setIsEditing(false);
      } else {
        resetForm();
        setIsEditing(true);
      }
    }
  }, [isOpen, channelId]);

  const loadChannelData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await channelApiClient.getById({ id: channelId! });
      if (response.success && response.data) {
        setFormData(response.data as unknown as ChannelFormData);
      } else {
        setError(response.error?.message || 'Failed to load Channel');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading Channel');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setError(null);
    setIsDirty(false);
  };

  const handleRefresh = async () => {
    if (channelId) {
      await loadChannelData();
    }
  };

  const handleChange = (field: keyof ChannelFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.ChannelName.trim()) {
      setError('Channel name is required');
      return;
    }

    setLoading(true);
    try {
      const response = channelId
        ? await channelApiClient.update(channelId, formData)
        : await channelApiClient.create(formData);

      if (response.success && response.data) {
        //  Auto-refresh related lookups
        if (!channelId) {
          lookupEventBus.emitRefresh('lookup_connector');
          lookupEventBus.emitRefresh('lookup_channel');
        }

        onChannelSaved?.(response.data as unknown as ChannelFormData);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save Channel');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving Channel');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showToast('info', 'Changes discarded');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div className="agent-editor-overlay" onClick={handleCancel}>
        <div
          className="agent-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {channelId ? 'Edit Channel' : 'Create Channel'}
            </h1>
            <div className="editor-header-actions">
              <button className="close-btn" onClick={handleCancel}>
                ✕
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && !channelId && (
            <div className="editor-loading">
              <Loader label="Loading..." />
            </div>
          )}

          {/* Content */}
          {!loading && (
            <form onSubmit={handleSave} className="editor-form">
              {/* Tabs */}
              <div className="top-tabs">
                {(['basic', 'webhook', 'webapi', 'content', 'security', 'debugger'] as ChannelTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'basic' && 'Basic'}
                    {tab === 'webhook' && 'Webhook'}
                    {tab === 'webapi' && 'Web API'}
                    {tab === 'content' && 'Content'}
                    {tab === 'security' && <><LockIcon size={12} /> Security</>}
                    {tab === 'debugger' && <><BugIcon size={12} /> Debugger</>}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Basic Tab */}
                {activeTab === 'basic' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Channel Name *</label>
                      <input
                        type="text"
                        value={formData.ChannelName}
                        onChange={(e) => handleChange('ChannelName', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Slack, Teams, Email"
                        maxLength={100}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Description</label>
                      <textarea
                        value={formData.Description}
                        onChange={(e) => handleChange('Description', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Detailed description"
                        rows={3}
                        maxLength={1000}
                      />
                    </div>

                    <div className="form-group">
                      <label>Short Description</label>
                      <input
                        type="text"
                        value={formData.ShortDescription}
                        onChange={(e) =>
                          handleChange('ShortDescription', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="Brief summary"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group">
                      <label>Icon URL</label>
                      <input
                        type="text"
                        value={formData.IconUrl}
                        onChange={(e) => handleChange('IconUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://example.com/icon.png"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group">
                      <label>Image URL</label>
                      <input
                        type="text"
                        value={formData.ImageUrl}
                        onChange={(e) => handleChange('ImageUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://example.com/image.png"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.connector}
                        value={formData.ConnectorID}
                        onChange={(value) => handleChange('ConnectorID', value)}
                        label="Connector"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select connector (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>
                  </div>
                )}

                {/* Webhook Tab */}
                {activeTab === 'webhook' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Webhook URL</label>
                      <input
                        type="text"
                        value={formData.WebHookUrl}
                        onChange={(e) => handleChange('WebHookUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://example.com/webhook"
                        maxLength={300}
                      />
                    </div>

                    <AccordionSection
                      title="Webhook Codes"
                      defaultExpanded={false}
                      icon={<LockIcon size={12} />}
                    >
                      <div className="form-grid">
                        {[
                          { field: 'WebHookCode', label: 'Code 1' },
                          { field: 'WebHookCode2', label: 'Code 2' },
                          { field: 'WebHookCode3', label: 'Code 3' },
                          { field: 'WebHookCode4', label: 'Code 4 (Long)' },
                        ].map(({ field, label }) => (
                          <div
                            key={field}
                            className={`form-group ${field === 'WebHookCode4' ? 'full' : ''}`}
                          >
                            <label>{label}</label>
                            <input
                              type="text"
                              value={
                                formData[field as keyof ChannelFormData] as string
                              }
                              onChange={(e) =>
                                handleChange(field as keyof ChannelFormData, e.target.value)
                              }
                              disabled={!isEditing}
                              placeholder={`Enter ${label.toLowerCase()}`}
                              maxLength={field === 'WebHookCode4' ? 1000 : 100}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionSection>
                  </div>
                )}

                {/* Web API Tab */}
                {activeTab === 'webapi' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Web API URL</label>
                      <input
                        type="text"
                        value={formData.WebApiUrl}
                        onChange={(e) => handleChange('WebApiUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://api.example.com/v1"
                        maxLength={300}
                      />
                    </div>

                    <AccordionSection
                      title="Web API Codes"
                      defaultExpanded={false}
                      icon={<LockIcon size={12} />}
                    >
                      <div className="form-grid">
                        {[
                          { field: 'WebApiCode', label: 'Code 1' },
                          { field: 'WebApiCode2', label: 'Code 2' },
                          { field: 'WebApiCode3', label: 'Code 3' },
                          { field: 'WebApiCode4', label: 'Code 4 (Long)' },
                        ].map(({ field, label }) => (
                          <div
                            key={field}
                            className={`form-group ${field === 'WebApiCode4' ? 'full' : ''}`}
                          >
                            <label>{label}</label>
                            <input
                              type="text"
                              value={
                                formData[field as keyof ChannelFormData] as string
                              }
                              onChange={(e) =>
                                handleChange(field as keyof ChannelFormData, e.target.value)
                              }
                              disabled={!isEditing}
                              placeholder={`Enter ${label.toLowerCase()}`}
                              maxLength={field === 'WebApiCode4' ? 1000 : 100}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionSection>
                  </div>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Help Documentation (Markdown)</label>
                      <textarea
                        value={formData.HelpDocumentation}
                        onChange={(e) =>
                          handleChange('HelpDocumentation', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="# Documentation\n\nMarkdown content here..."
                        rows={8}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Long Description</label>
                      <textarea
                        value={formData.LongDescription}
                        onChange={(e) =>
                          handleChange('LongDescription', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="Extended description"
                        rows={4}
                      />
                    </div>
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="form-grid read-only">
                    <div className="form-group">
                      <label>Primary ID</label>
                      <div className="display-value">{formData.ChannelID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Resource ID (ResID)</label>
                      <div className="display-value">{formData.ResID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Created On</label>
                      <div className="display-value">
                        {formData.CreatedOn ? new Date(formData.CreatedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Created By</label>
                      <div className="display-value">{formData.CreatedBy || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified On</label>
                      <div className="display-value">
                        {formData.LastModifiedOn ? new Date(formData.LastModifiedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified By</label>
                      <div className="display-value">{formData.LastModifiedBy || 'N/A'}</div>
                    </div>
                  </div>
                )}

                {/* Debugger Tab */}
                {activeTab === 'debugger' && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc', fontSize: '14px' }}><ClipboardIcon size={12} /> Entity Data (JSON)</h4>
                      <textarea
                        value={JSON.stringify(formData, null, 2)}
                        readOnly
                        style={{
                          width: '100%',
                          height: '400px',
                          padding: '12px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(58, 140, 69, 0.3)',
                          borderRadius: '6px',
                          color: '#3A8C45',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          resize: 'vertical',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      <LightbulbIcon size={12} /> Tip: Copy this JSON data for debugging or API requests
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="editor-footer">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        resetForm();
                        if (channelId) loadChannelData();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    {channelId && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Refresh data from server"
                      >
                        <ZapIcon size={14} /> Refresh
                      </button>
                    )}
                    {channelId && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                        title="Enable edit mode"
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ChannelEditor;
